export type InvoiceStatus = 'issued' | 'created' | 'sent_to_invoicing_service';

export type InvoiceLogs = {
  event: InvoiceStatus;
  created_at: {
    $date: number;
  };
};

export type InvoiceType =
  | 'wolt_invoice'
  | 'merchant_invoice'
  | 'photos_invoice'
  | 'printer_invoice'
  | 'ipad_invoice'
  | 'credit_note'
  | 'correction_invoice';

export type Invoices = {
  idempotency_id: string;
  invoice_type: InvoiceType;
  invoicing_service_id: string;
  logs: InvoiceLogs[];
  manual_approval: boolean;
  status: InvoiceStatus;
};

export type InvoiceDossierStatus =
  | 'created'
  | 'supplementaries_rendered'
  | 'invoices_sent_to_invoicing_service'
  | 'payment_documents_sent_to_invoicing_service'
  | 'payment_documents_issued'
  | 'invoices_drafted'
  | 'invoices_approved'
  | 'invoices_issued'
  | 'documents_sent_to_merchant'
  | 'transfer_released';

export type InvoiceDossierLogs = {
  event: InvoiceDossierStatus;
  created_at: {
    $date: number;
  };
};

export type SupplementariesDocumentType = 'sales_report' | 'netting_report';

export type Supplementaries = {
  document_type: SupplementariesDocumentType;
  url: string;
};

export type PaymentDocumentStatus = 'created' | 'sent_to_invoicing_service' | 'issued';

export type PaymentDocument = {
  idempotency_id: string;
  type: SupplementariesDocumentType;
  status: PaymentDocumentStatus;
  invoicing_service_id: string;
};

export type InvoiceDossier = {
  country: string;
  created_at: { $date: number };
  invoices: Invoices[];
  logs: InvoiceDossierLogs;
  merchant: { $oid: string };
  status: InvoiceDossierStatus;
  supplementaries: Supplementaries[];
  payment_documents: PaymentDocument[];
  venue: { $oid: string };
  venue_payout: number;
  _id: { $oid: string };
};

export type ProductType =
  | 'ipad'
  | 'sim'
  | 'printer'
  | 'other'
  | 'merchant_gear'
  | 'free_delivery'
  | 'photos'
  | 'cashback'
  | 'cashback_by_merchant'
  | 'app_visibility'
  | 'sponsored_listings'
  | 'credit_sale_merchant'
  | 'insolvency_deduction'
  | 'android_nokia_wifi'
  | 'android_nokia_wifi_cellular'
  | 'android_lenovo_wifi'
  | 'android_lenovo_wifi_cellular'
  | 'ipad_wifi'
  | 'ipad_wifi_cellular'
  | 'samsung_a04'
  | 'android_lenovo_m10_wifi'
  | 'android_lenovo_m10_wifi_cellular'
  | 'merchant_platform_fee'
  | 'commission';
export type DeductionType = 'fixed' | 'recurring' | 'free';
export type ConditionType = 'new' | 'used' | 'own_device' | null;
export type FreeReasonType = 'stolen' | 'broken' | 'complimentary' | 'other';
export type ProductSalesInvoice = {
  external_invoice_id: string;
  invoice_id: string;
};
export type Product = {
  amount: number;
  apply_at?: { $date: number };
  created_at?: { $date: number };
  created_by?: { $oid: string };
  currency: Currency;
  deduction_type: DeductionType;
  condition?: ConditionType;
  end_at?: { $date: number };
  free_product_reason?: FreeReasonType;
  id: { $oid: string };
  max_payout_percentage?: number;
  min_payout_left?: number;
  min_tranches?: number;
  modified_at?: { $date: number };
  note?: string;
  product_type: ProductType;
  serial_number?: string;
  recurring_interval?: string;
  settled: boolean;
  start_at?: { $date: number };
  transactions: { applied_at: { $date: number }; paid_amount: number }[];
  vat_percentage: number;
  venue_id: string;
  invoice?: ProductSalesInvoice;
};

export type Products = {
  results: Product[];
};

export type ProductFormTypes = ProductType;

// DO NOT CHANGE THE VALUES IN THIS MAPPING - at least for the devices
// they're used as prefixes for payout reports. We rely on the names existing exactly as they are written here to add/remove
// the prefix. You can change the display name in the dropdown by modifying productTypeOptions in ProductSalesForm.tsx
export const productNameMapping: Record<ProductFormTypes, string> = {
  sim: 'SIM card',
  ipad: 'iPad',
  ipad_wifi: 'iPad Wifi',
  ipad_wifi_cellular: 'iPad Wifi + Cellular',
  android_nokia_wifi: 'Android Tab Nokia T10 WIFI',
  android_nokia_wifi_cellular: 'Android Tab Nokia T10 Wifi+Cellular',
  android_lenovo_wifi: 'Android Lenovo Tab M8 WIFI',
  android_lenovo_wifi_cellular: 'Android Tab M8 Wifi+Cellular',
  android_lenovo_m10_wifi: 'Android Lenovo Tab M10 WIFI',
  android_lenovo_m10_wifi_cellular: 'Android Tab M10 Wifi+Cellular',
  samsung_a04: 'Samsung A04',
  printer: 'Printer',
  merchant_gear: 'Merchant gear',
  other: 'Other',
  free_delivery: 'Free delivery',
  photos: 'Photos',
  cashback: 'Cashback partial',
  cashback_by_merchant: 'Cashback covered by merchant',
  app_visibility: 'App visibility',
  sponsored_listings: 'Sponsored listings',
  credit_sale_merchant: 'Credit sales merchant',
  insolvency_deduction: 'Insolvency deduction',
  commission: 'Commission',
  merchant_platform_fee: 'Merchant platform fee',
};
